import React from "react";
import { Link } from "react-router-dom";
import { Clock, Users, Check, ArrowRight } from "lucide-react";
import { useSite } from "../lib/SiteContext";
import { mediaUrl } from "../lib/api";

const ChartersSection = () => {
  const { charters } = useSite();
  const active = (charters || []).filter(
    (c) => c.active !== false && (c.type || "own") === "own"
  );

  return (
    <section id="charters" className="section-padding bg-background" data-testid="charters-section">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14 md:mb-20">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Our Charters</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-primary text-balance">
              Two ways to spend a perfect day on the Maltese sea.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <p className="text-muted-foreground leading-relaxed">
              Every charter is private — just you and your guests, your skipper, and the open Mediterranean. No fixed
              route, no shared boats. Choose half a day or the whole day.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {active.map((c, i) => (
            <article
              key={c.id}
              className="group bg-white border border-border hover:border-primary/30 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl flex flex-col"
              data-testid={`charter-card-${c.slug}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={mediaUrl(c.image_url)}
                  alt={c.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute top-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-sm text-[10px] tracking-[0.25em] uppercase text-primary font-semibold">
                  {c.duration_hours}h
                </div>
              </div>

              <div className="p-7 flex-1 flex flex-col">
                <div className="flex items-center gap-4 text-xs uppercase tracking-[0.2em] text-secondary mb-3">
                  <span className="flex items-center gap-1.5">
                    <Clock size={12} /> {c.start_time}–{c.end_time}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Users size={12} /> Up to {c.max_guests}
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-primary leading-tight mb-2">
                  {c.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-5 leading-relaxed">{c.subtitle}</p>

                <ul className="space-y-2 mb-6">
                  {(c.inclusions || []).map((inc, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-foreground/80">
                      <Check size={14} className="text-secondary mt-0.5 shrink-0" />
                      {inc}
                    </li>
                  ))}
                </ul>

                <div className="hairline mb-5" />
                <div className="flex items-end justify-between mt-auto">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">From</div>
                    <div className="font-serif text-3xl text-primary">€{Number(c.price_eur).toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">deposit €{Number(c.deposit_eur).toLocaleString()}</div>
                  </div>
                  <Link
                    to={`/book?charter=${c.id}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors"
                    style={{ borderRadius: "2px" }}
                    data-testid={`book-charter-${c.slug}`}
                  >
                    Book <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ChartersSection;
